import { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell } from "recharts";

/* ============================================================
   VERATRACK — Production Tracking Demo v2
   Full inline styles — no Tailwind dependency
   ============================================================ */

const C = {
  bg: "#0b0f14", surface: "#121924", surface2: "#1a2432", line: "#243248",
  text: "#e8edf4", muted: "#8496ad", amber: "#f5a623", amberDim: "#3a2c10",
  green: "#4ade80", greenDim: "#0f2b1a", red: "#f87171", redDim: "#331416",
  blue: "#60a5fa", blueDim: "#12233d", cyan: "#22d3ee",
};

const F = { heading: "'Barlow Condensed',sans-serif", body: "'IBM Plex Sans',sans-serif", mono: "'JetBrains Mono',monospace" };

const STATION_COLORS = { Flooring: C.amber, Plumbing: C.cyan, Electrical: C.blue };

const STATUS_META = {
  not_started: { label: "Not Started", fg: C.muted, bg: "#1c2634" },
  in_progress:  { label: "In Progress",  fg: C.blue,  bg: C.blueDim },
  submitted:    { label: "Awaiting QC",  fg: C.amber, bg: C.amberDim },
  approved:     { label: "Approved",     fg: C.green, bg: C.greenDim },
  rejected:     { label: "Rejected",     fg: C.red,   bg: C.redDim },
};

/* ---- seed data ---- */
const SEED_USERS = [
  { id:"u1", name:"Jairo Estrada",  email:"jairo@fastbaysolutions.com", role:"admin",      provider:"entra",      active:true  },
  { id:"u2", name:"Marisol Vega",   email:"m.vega@verabuilt.com",       role:"supervisor", provider:"entra",      active:true  },
  { id:"u3", name:"Dan Okafor",     email:"d.okafor@verabuilt.com",     role:"worker",     provider:"standalone", active:true  },
  { id:"u4", name:"Lena Fields",    email:"l.fields@verabuilt.com",     role:"worker",     provider:"entra",      active:true  },
  { id:"u5", name:"Ray Soto",       email:"r.soto@verabuilt.com",       role:"worker",     provider:"standalone", active:false },
];

const SEED_TREE = [{
  id:"p1", name:"Project Vera", client:"Vera Built Homes", status:"active",
  units:[
    { id:"hu1", code:"BLT004", model:"Ridgeline 2BR", status:"in_production", stations:[
      { id:"s1", name:"Flooring", seq:1, tasks:[
        { id:"t1", name:"Subfloor Install", subtasks:[
          { id:"st1", name:"Lay joist insulation",   status:"approved",    assignedTo:"u3", est:2.0 },
          { id:"st2", name:"Fasten OSB decking",     status:"approved",    assignedTo:"u3", est:3.0 },
        ]},
        { id:"t2", name:"Finish Flooring", subtasks:[
          { id:"st3", name:"LVP main living area",   status:"submitted",   assignedTo:"u4", est:4.0 },
          { id:"st4", name:"Transition strips+trim", status:"not_started", assignedTo:"u4", est:1.5 },
        ]},
      ]},
      { id:"s2", name:"Plumbing", seq:2, tasks:[
        { id:"t3", name:"Rough-In", subtasks:[
          { id:"st5", name:"PEX supply runs",        status:"in_progress", assignedTo:"u3", est:3.5 },
          { id:"st6", name:"DWV stack + venting",    status:"not_started", assignedTo:"u3", est:4.0 },
        ]},
        { id:"t4", name:"Fixture Set", subtasks:[
          { id:"st7", name:"Set kitchen sink+faucet",status:"not_started", assignedTo:"u4", est:1.5 },
        ]},
      ]},
      { id:"s3", name:"Electrical", seq:3, tasks:[
        { id:"t5", name:"Rough Wiring", subtasks:[
          { id:"st8", name:"Pull homerun circuits",  status:"rejected",    assignedTo:"u4", est:3.0 },
          { id:"st9", name:"Box + device rough-in",  status:"not_started", assignedTo:"u4", est:2.5 },
        ]},
        { id:"t6", name:"Panel & Trim", subtasks:[
          { id:"st10",name:"Land breakers at panel", status:"not_started", assignedTo:"u3", est:2.0 },
        ]},
      ]},
    ]},
    { id:"hu2", code:"BLT005", model:"Ridgeline 3BR", status:"queued", stations:[
      { id:"s4", name:"Flooring", seq:1, tasks:[
        { id:"t7", name:"Subfloor Install", subtasks:[
          { id:"st11",name:"Lay joist insulation",   status:"not_started", assignedTo:"u4", est:2.0 },
        ]},
      ]},
    ]},
  ],
}];

const SEED_TIME = [
  { id:"te1", subtaskId:"st1",  userId:"u3", station:"Flooring",   hours:1.8, day:"Mon" },
  { id:"te2", subtaskId:"st2",  userId:"u3", station:"Flooring",   hours:3.4, day:"Mon" },
  { id:"te3", subtaskId:"st3",  userId:"u4", station:"Flooring",   hours:4.6, day:"Tue" },
  { id:"te4", subtaskId:"st5",  userId:"u3", station:"Plumbing",   hours:2.1, day:"Tue" },
  { id:"te5", subtaskId:"st8",  userId:"u4", station:"Electrical", hours:3.9, day:"Wed" },
  { id:"te6", subtaskId:"st5",  userId:"u3", station:"Plumbing",   hours:1.2, day:"Wed" },
];

const SEED_SIGNOFFS = [
  { id:"so1", subtaskId:"st1", step:"worker",     by:"u3", action:"submitted", note:"",                          at:"Mon 14:12" },
  { id:"so2", subtaskId:"st1", step:"supervisor", by:"u2", action:"approved",  note:"Verified spacing",          at:"Mon 15:02" },
  { id:"so3", subtaskId:"st2", step:"worker",     by:"u3", action:"submitted", note:"",                          at:"Mon 16:40" },
  { id:"so4", subtaskId:"st2", step:"supervisor", by:"u2", action:"approved",  note:"",                          at:"Mon 17:05" },
  { id:"so5", subtaskId:"st3", step:"worker",     by:"u4", action:"submitted", note:"Ready for QC",              at:"Tue 13:30" },
  { id:"so6", subtaskId:"st8", step:"worker",     by:"u4", action:"submitted", note:"",                          at:"Wed 11:20" },
  { id:"so7", subtaskId:"st8", step:"supervisor", by:"u2", action:"rejected",  note:"Circuit 7 mislabeled — relabel and resubmit", at:"Wed 11:55" },
];

/* ---- helpers ---- */
function fmtClock(sec) {
  const h=Math.floor(sec/3600), m=Math.floor((sec%3600)/60), s=Math.floor(sec%60);
  return `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`;
}
function findSubtask(tree, stId) {
  for (const p of tree) for (const u of p.units) for (const s of u.stations) for (const t of s.tasks)
    for (const st of t.subtasks) if (st.id===stId) return {p,u,s,t,st};
  return null;
}
function mutTree(tree, stId, fn) {
  return tree.map(p=>({...p, units:p.units.map(u=>({...u, stations:u.stations.map(s=>({...s, tasks:s.tasks.map(t=>({...t, subtasks:t.subtasks.map(st=>st.id===stId?fn(st):st)}))}))}))})  );
}

/* ---- shared UI ---- */
const s = {
  card: { background:C.surface, border:`1px solid ${C.line}`, borderRadius:10, padding:16 },
  cardNoPad: { background:C.surface, border:`1px solid ${C.line}`, borderRadius:10, overflow:"hidden" },
  input: { background:C.surface2, border:`1px solid ${C.line}`, borderRadius:6, padding:"8px 12px", color:C.text, fontSize:13, outline:"none", fontFamily:F.body, width:"100%", boxSizing:"border-box" },
  row: { display:"flex", flexWrap:"wrap", gap:8, alignItems:"center" },
};

function Chip({ status }) {
  const m = STATUS_META[status]||STATUS_META.not_started;
  return <span style={{ background:m.bg, color:m.fg, padding:"3px 8px", borderRadius:4, fontSize:11, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", whiteSpace:"nowrap" }}>{m.label}</span>;
}

function Btn({ children, onClick, kind="ghost", disabled, small }) {
  const kinds = {
    primary: { background:C.amber,   color:"#171006", border:`1px solid ${C.amber}` },
    green:   { background:C.green,   color:"#06210f", border:`1px solid ${C.green}` },
    red:     { background:"transparent", color:C.red, border:`1px solid ${C.red}`   },
    ghost:   { background:"transparent", color:C.text,border:`1px solid ${C.line}`  },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      ...kinds[kind], borderRadius:6, padding: small?"4px 10px":"7px 14px",
      fontSize: small?11:13, fontWeight:600, fontFamily:F.body, cursor:disabled?"not-allowed":"pointer",
      opacity:disabled?0.4:1, whiteSpace:"nowrap",
    }}>{children}</button>
  );
}

function SectionTitle({ kicker, title }) {
  return (
    <div style={{ marginBottom:20 }}>
      {kicker && <div style={{ color:C.amber, fontSize:11, fontWeight:700, letterSpacing:"0.2em", textTransform:"uppercase", marginBottom:4 }}>{kicker}</div>}
      <div style={{ fontFamily:F.heading, fontSize:28, letterSpacing:"0.01em", color:C.text }}>{title}</div>
    </div>
  );
}

function TabBar({ tabs, active, onChange }) {
  return (
    <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:20 }}>
      {tabs.map(([id,label])=>(
        <button key={id} onClick={()=>onChange(id)} style={{
          padding:"7px 14px", borderRadius:6, fontSize:13, fontWeight:600, fontFamily:F.body, cursor:"pointer",
          background: active===id ? C.amber : C.surface,
          color: active===id ? "#171006" : C.muted,
          border: `1px solid ${active===id ? C.amber : C.line}`,
        }}>{label}</button>
      ))}
    </div>
  );
}

/* ============================================================ LOGIN */
function LoginScreen({ users, settings, onLogin }) {
  const [email,setEmail]=useState(""); const [pw,setPw]=useState(""); const [err,setErr]=useState("");
  const domainOk=em=>settings.domains.some(d=>em.toLowerCase().endsWith("@"+d.toLowerCase()));

  const tryStandalone=()=>{
    setErr("");
    if(settings.authMode==="entra_only") return setErr("Standalone login disabled. Use Microsoft Entra.");
    if(!domainOk(email)) return setErr("Email domain not approved for access.");
    const u=users.find(x=>x.email.toLowerCase()===email.toLowerCase()&&x.active);
    if(!u||!pw) return setErr("Invalid credentials or inactive account.");
    onLogin(u);
  };
  const tryEntra=u=>{
    setErr("");
    if(settings.authMode==="standalone_only") return setErr("Entra SSO disabled by administrator.");
    if(!domainOk(u.email)) return setErr("Email domain not approved.");
    if(!u.active) return setErr("Account is deactivated.");
    onLogin(u);
  };

  return (
    <div style={{ minHeight:"100vh", display:"flex", alignItems:"center", justifyContent:"center", padding:24, background:C.bg,
      backgroundImage:`radial-gradient(ellipse 60% 40% at 70% 0%, ${C.amberDim}88, transparent), repeating-linear-gradient(0deg,transparent,transparent 39px,${C.line}33 40px),repeating-linear-gradient(90deg,transparent,transparent 39px,${C.line}33 40px)` }}>
      <div style={{ width:"100%", maxWidth:420 }}>
        <div style={{ marginBottom:32 }}>
          <div style={{ color:C.amber, fontSize:11, fontWeight:700, letterSpacing:"0.3em", textTransform:"uppercase", marginBottom:4 }}>Fast Bay Solutions</div>
          <div style={{ fontFamily:F.heading, fontSize:52, lineHeight:1, color:C.text, letterSpacing:"0.01em" }}>
            VERA<span style={{ color:C.amber }}>TRACK</span>
          </div>
          <div style={{ color:C.muted, fontSize:13, marginTop:4 }}>Modular Home Production Tracking · Demo</div>
        </div>

        <div style={{ ...s.card, marginBottom:12 }}>
          <button onClick={()=>tryEntra(users[0])} style={{ width:"100%", display:"flex", alignItems:"center", justifyContent:"center", gap:10, padding:"10px 16px", borderRadius:6, background:"#fff", color:"#111", border:"none", fontWeight:600, fontSize:13, cursor:"pointer", fontFamily:F.body }}>
            <svg width="16" height="16" viewBox="0 0 16 16"><rect x="0" y="0" width="7.4" height="7.4" fill="#f25022"/><rect x="8.6" y="0" width="7.4" height="7.4" fill="#7fba00"/><rect x="0" y="8.6" width="7.4" height="7.4" fill="#00a4ef"/><rect x="8.6" y="8.6" width="7.4" height="7.4" fill="#ffb900"/></svg>
            Sign in with Microsoft Entra
          </button>
          <div style={{ display:"flex", alignItems:"center", gap:12, margin:"16px 0" }}>
            <div style={{ flex:1, height:1, background:C.line }} />
            <span style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.15em" }}>or standalone</span>
            <div style={{ flex:1, height:1, background:C.line }} />
          </div>
          <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="email@verabuilt.com" style={{ ...s.input, marginBottom:8 }} />
          <input value={pw} onChange={e=>setPw(e.target.value)} type="password" placeholder="Password" style={{ ...s.input, marginBottom:12 }} />
          <Btn kind="primary" onClick={tryStandalone}><span style={{ display:"block", textAlign:"center", width:"100%" }}>Sign In</span></Btn>
          {err && <div style={{ marginTop:12, background:C.redDim, color:C.red, padding:"8px 12px", borderRadius:6, fontSize:12 }}>{err}</div>}
        </div>

        <div style={s.card}>
          <div style={{ color:C.muted, fontSize:11, textTransform:"uppercase", letterSpacing:"0.15em", fontWeight:700, marginBottom:10 }}>Demo — quick sign-in as</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
            {users.filter(u=>u.active).map(u=>(
              <button key={u.id} onClick={()=>onLogin(u)} style={{ textAlign:"left", padding:"8px 12px", borderRadius:6, background:C.surface2, border:`1px solid ${C.line}`, cursor:"pointer" }}>
                <div style={{ color:C.text, fontWeight:600, fontSize:13, fontFamily:F.body }}>{u.name}</div>
                <div style={{ color:C.amber, fontSize:10, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.12em" }}>{u.role}</div>
              </button>
            ))}
          </div>
          <div style={{ marginTop:12, fontSize:11, color:C.muted }}>
            Auth mode: <span style={{ color:C.text }}>{settings.authMode}</span> · Domains: <span style={{ color:C.text }}>{settings.domains.join(", ")}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============================================================ TRACKER */
function Tracker({ user, tree, setTree, timers, setTimers, now, addTime, addSignOff, signOffs }) {
  const [path,setPath]=useState({p:null,u:null,s:null,t:null});
  const isWorker=user.role==="worker";

  const setStatus=(stId,status)=>setTree(prev=>mutTree(prev,stId,st=>({...st,status})));
  const startTimer=st=>setTimers(tm=>({...tm,[st.id]:Date.now()}));
  const stopTimer=st=>{
    const started=timers[st.id]; if(!started) return;
    const hrs=Math.max((Date.now()-started)/3600000,0.01);
    addTime(st,hrs);
    setTimers(tm=>{const c={...tm};delete c[st.id];return c;});
    if(st.status==="not_started") setStatus(st.id,"in_progress");
  };
  const submit=st=>{ stopTimer(st); setStatus(st.id,"submitted"); addSignOff({subtaskId:st.id,step:"worker",by:user.id,action:"submitted",note:""}); };

  const crumbs=[
    {label:"Projects",onClick:()=>setPath({p:null,u:null,s:null,t:null})},
    path.p&&{label:path.p.name,onClick:()=>setPath({p:path.p,u:null,s:null,t:null})},
    path.u&&{label:path.u.code,onClick:()=>setPath({...path,s:null,t:null})},
    path.s&&{label:path.s.name,onClick:()=>setPath({...path,t:null})},
    path.t&&{label:path.t.name,onClick:()=>{}},
  ].filter(Boolean);

  return (
    <div>
      <SectionTitle kicker="Production" title="Task Time Tracking" />
      <div style={{ display:"flex", flexWrap:"wrap", gap:6, marginBottom:16, fontSize:13, color:C.muted }}>
        {crumbs.map((c,i)=>(
          <span key={i} style={{ display:"flex", alignItems:"center", gap:6 }}>
            {i>0 && <span style={{ color:C.line }}>›</span>}
            <button onClick={c.onClick} style={{ background:"none", border:"none", cursor:"pointer", color:i===crumbs.length-1?C.text:C.amber, fontFamily:F.body, fontSize:13, padding:0 }}>{c.label}</button>
          </span>
        ))}
      </div>

      {!path.p && (
        <div style={{ display:"grid", gap:12 }}>
          {tree.map(p=>(
            <div key={p.id} style={{ ...s.card, cursor:"pointer" }} onClick={()=>setPath({p,u:null,s:null,t:null})}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <div>
                  <div style={{ fontFamily:F.heading, fontSize:22, color:C.text }}>{p.name}</div>
                  <div style={{ color:C.muted, fontSize:12, marginTop:2 }}>{p.client} · {p.units.length} home unit(s)</div>
                </div>
                <span style={{ color:C.amber, fontSize:22 }}>›</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {path.p&&!path.u && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(240px,1fr))", gap:12 }}>
          {path.p.units.map(u=>(
            <div key={u.id} style={{ ...s.card, cursor:"pointer" }} onClick={()=>setPath({...path,u})}>
              <div style={{ display:"flex", justifyContent:"space-between" }}>
                <div>
                  <div style={{ fontFamily:F.mono, fontSize:22, color:C.amber, fontWeight:700 }}>{u.code}</div>
                  <div style={{ fontSize:14, color:C.text, marginTop:2 }}>{u.model}</div>
                  <div style={{ fontSize:11, color:C.muted, marginTop:4, textTransform:"uppercase", letterSpacing:"0.1em" }}>{u.status.replace("_"," ")}</div>
                </div>
                <span style={{ color:C.amber, fontSize:22 }}>›</span>
              </div>
            </div>
          ))}
        </div>
      )}

      {path.u&&!path.s && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))", gap:12 }}>
          {path.u.stations.map(s2=>{
            const all=s2.tasks.flatMap(t=>t.subtasks);
            const done=all.filter(x=>x.status==="approved").length;
            const pct=all.length?(done/all.length)*100:0;
            return (
              <div key={s2.id} style={{ ...s.card, cursor:"pointer" }} onClick={()=>setPath({...path,s:s2})}>
                <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:8 }}>
                  <span style={{ width:10, height:10, borderRadius:"50%", background:STATION_COLORS[s2.name]||C.muted, display:"inline-block" }} />
                  <span style={{ fontFamily:F.heading, fontSize:20 }}>{s2.name}</span>
                </div>
                <div style={{ fontSize:12, color:C.muted, marginBottom:8 }}>{done}/{all.length} subtasks approved</div>
                <div style={{ height:6, borderRadius:3, background:C.surface2, overflow:"hidden" }}>
                  <div style={{ height:"100%", borderRadius:3, width:`${pct}%`, background:STATION_COLORS[s2.name]||C.amber }} />
                </div>
              </div>
            );
          })}
        </div>
      )}

      {path.s && (
        <div style={{ display:"grid", gap:16 }}>
          {path.s.tasks.map(t=>(
            <div key={t.id} style={s.cardNoPad}>
              <div style={{ padding:"10px 16px", background:C.surface2, borderBottom:`1px solid ${C.line}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
                <span style={{ fontFamily:F.heading, fontSize:18 }}>{t.name}</span>
                <span style={{ color:C.muted, fontSize:12 }}>{t.subtasks.length} subtask(s)</span>
              </div>
              {t.subtasks.map(st=>{
                const running=timers[st.id];
                const mine=st.assignedTo===user.id;
                const canWork=(!isWorker||mine)&&["not_started","in_progress","rejected"].includes(st.status);
                const lastReject=signOffs.filter(x=>x.subtaskId===st.id&&x.action==="rejected").slice(-1)[0];
                return (
                  <div key={st.id} style={{ padding:"12px 16px", borderBottom:`1px solid ${C.line}`, display:"flex", flexWrap:"wrap", gap:10, alignItems:"center" }}>
                    <div style={{ flex:1, minWidth:180 }}>
                      <div style={{ fontSize:14, fontWeight:500 }}>{st.name}</div>
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>Est {st.est}h · {mine?"Assigned to you":"Assigned: "+st.assignedTo}</div>
                      {st.status==="rejected"&&lastReject?.note && <div style={{ fontSize:11, color:C.red, marginTop:4 }}>⚠ {lastReject.note}</div>}
                    </div>
                    <Chip status={running?"in_progress":st.status} />
                    <span style={{ fontFamily:F.mono, fontSize:13, color:running?C.green:C.muted, minWidth:80, textAlign:"right" }}>
                      {running?fmtClock((now-running)/1000):"—"}
                    </span>
                    {canWork&&!running && <Btn small kind="primary" onClick={()=>startTimer(st)}>▶ Start</Btn>}
                    {running && <Btn small kind="red" onClick={()=>stopTimer(st)}>■ Stop</Btn>}
                    {canWork && <Btn small onClick={()=>submit(st)} disabled={st.status==="not_started"&&!running}>Submit for Sign-Off</Btn>}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ============================================================ APPROVALS */
function Approvals({ user, tree, setTree, signOffs, addSignOff, users }) {
  const [notes,setNotes]=useState({});
  const pending=[];
  for(const p of tree) for(const u of p.units) for(const s of u.stations) for(const t of s.tasks)
    for(const st of t.subtasks) if(st.status==="submitted") pending.push({p,u,s,t,st});

  const setStatus=(stId,status)=>setTree(prev=>mutTree(prev,stId,st=>({...st,status})));
  const decide=(st,action)=>{
    setStatus(st.id,action==="approved"?"approved":"rejected");
    addSignOff({subtaskId:st.id,step:"supervisor",by:user.id,action,note:notes[st.id]||""});
    setNotes(n=>({...n,[st.id]:""}));
  };
  const nameOf=id=>users.find(u=>u.id===id)?.name||id;

  return (
    <div>
      <SectionTitle kicker="Quality Control" title="Sign-Off Queue" />
      {pending.length===0 && <div style={{ ...s.card, textAlign:"center", color:C.muted, padding:32 }}>No subtasks awaiting sign-off ✓</div>}
      <div style={{ display:"grid", gap:12, marginBottom:32 }}>
        {pending.map(({p,u,s2,t,st,...rest})=>{
          const item=rest; const pu=rest.u||u; const ps=rest.s||s2;
          const found=findSubtask(tree,st.id);
          const workerSubmit=signOffs.filter(x=>x.subtaskId===st.id&&x.step==="worker").slice(-1)[0];
          return (
            <div key={st.id} style={s.card}>
              <div style={{ ...s.row, justifyContent:"space-between", marginBottom:12 }}>
                <div>
                  <div style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>
                    {found?.p.name} › <span style={{ fontFamily:F.mono, color:C.amber }}>{found?.u.code}</span> › {found?.s.name} › {found?.t.name}
                  </div>
                  <div style={{ fontWeight:600, fontSize:15 }}>{st.name}</div>
                  <div style={{ fontSize:12, color:C.muted, marginTop:4 }}>
                    Submitted by {nameOf(st.assignedTo)}{workerSubmit?` · ${workerSubmit.at||"just now"}`:""}
                    {workerSubmit?.note&&` · "${workerSubmit.note}"`}
                  </div>
                </div>
                <Chip status="submitted" />
              </div>
              <div style={s.row}>
                <input value={notes[st.id]||""} onChange={e=>setNotes(n=>({...n,[st.id]:e.target.value}))}
                  placeholder="QC note (required on reject)…" style={{ ...s.input, flex:1, minWidth:200, width:"auto" }} />
                <Btn small kind="green" onClick={()=>decide(st,"approved")}>✓ Approve</Btn>
                <Btn small kind="red" onClick={()=>decide(st,"rejected")} disabled={!(notes[st.id]||"").trim()}>✕ Reject</Btn>
              </div>
            </div>
          );
        })}
      </div>

      <SectionTitle kicker="Audit Trail" title="Recent Sign-Off Events" />
      <div style={s.cardNoPad}>
        {signOffs.slice(-8).reverse().map(e=>{
          const f=findSubtask(tree,e.subtaskId);
          return (
            <div key={e.id} style={{ padding:"10px 16px", borderBottom:`1px solid ${C.line}`, display:"flex", flexWrap:"wrap", gap:8, alignItems:"center", fontSize:12 }}>
              <span style={{ fontFamily:F.mono, color:C.muted, fontSize:11 }}>{e.at||"now"}</span>
              <span style={{ fontWeight:600 }}>{users.find(u=>u.id===e.by)?.name||e.by}</span>
              <span style={{ color:e.action==="approved"?C.green:e.action==="rejected"?C.red:C.amber, textTransform:"uppercase", fontWeight:700, fontSize:11, letterSpacing:"0.08em" }}>{e.action}</span>
              <span style={{ color:C.muted }}>{f?`${f.st.name} (${f.u.code} · ${f.s.name})`:e.subtaskId}</span>
              {e.note&&<span style={{ color:C.muted }}>— "{e.note}"</span>}
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ============================================================ DASHBOARD */
function Dashboard({ user, tree, timeEntries, users, visibility }) {
  const [fStation,setFStation]=useState("all"); const [fWorker,setFWorker]=useState("all");
  const isWorker=user.role==="worker";

  const entries=timeEntries.filter(e=>(isWorker?e.userId===user.id:true)&&(fStation==="all"||e.station===fStation)&&(fWorker==="all"||e.userId===fWorker));
  const allSubs=tree.flatMap(p=>p.units.flatMap(u=>u.stations.flatMap(s=>s.tasks.flatMap(t=>t.subtasks.map(st=>({...st,station:s.name}))))));
  const scoped=isWorker?allSubs.filter(s=>s.assignedTo===user.id):allSubs;

  const totalHrs=entries.reduce((a,b)=>a+b.hours,0);
  const approved=scoped.filter(s=>s.status==="approved").length;
  const pendingQC=scoped.filter(s=>s.status==="submitted").length;
  const rejected=scoped.filter(s=>s.status==="rejected").length;

  const byStation=["Flooring","Plumbing","Electrical"].map(st=>({
    name:st, hours:+entries.filter(e=>e.station===st).reduce((a,b)=>a+b.hours,0).toFixed(1)
  }));
  const estVsActual=scoped.filter(s=>["approved","submitted","in_progress","rejected"].includes(s.status)).map(s=>{
    const actual=timeEntries.filter(e=>e.subtaskId===s.id).reduce((a,b)=>a+b.hours,0);
    return { name:s.name.length>18?s.name.slice(0,18)+"…":s.name, est:s.est, actual:+actual.toFixed(1) };
  }).filter(r=>r.actual>0);

  const kpis=[
    {label:"Logged Hours",value:totalHrs.toFixed(1),color:C.amber},
    {label:"Approved",    value:approved,            color:C.green},
    {label:"Awaiting QC", value:pendingQC,           color:C.blue},
    {label:"Rejected",    value:rejected,             color:C.red},
  ];

  const selStyle={ ...s.input, width:"auto", padding:"6px 12px" };

  return (
    <div>
      <SectionTitle kicker={isWorker?"My Performance":"Plant Analytics"} title={isWorker?"My Dashboard":"Production Dashboard"} />
      <div style={{ ...s.row, marginBottom:16 }}>
        <select value={fStation} onChange={e=>setFStation(e.target.value)} style={selStyle}>
          <option value="all">All Stations</option>
          {["Flooring","Plumbing","Electrical"].map(s=><option key={s} value={s}>{s}</option>)}
        </select>
        {!isWorker&&(
          <select value={fWorker} onChange={e=>setFWorker(e.target.value)} style={selStyle}>
            <option value="all">All Workers</option>
            {users.filter(u=>u.role==="worker").map(u=><option key={u.id} value={u.id}>{u.name}</option>)}
          </select>
        )}
        <span style={{ background:C.surface2, border:`1px solid ${C.line}`, borderRadius:6, padding:"4px 10px", fontSize:11, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em" }}>
          Fixed view · {user.role}
        </span>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(160px,1fr))", gap:12, marginBottom:20 }}>
        {kpis.map(k=>(
          <div key={k.label} style={s.card}>
            <div style={{ fontFamily:F.mono, fontSize:32, fontWeight:700, color:k.color }}>{k.value}</div>
            <div style={{ fontSize:11, color:C.muted, textTransform:"uppercase", letterSpacing:"0.12em", marginTop:4 }}>{k.label}</div>
          </div>
        ))}
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(320px,1fr))", gap:16 }}>
        <div style={s.card}>
          <div style={{ fontFamily:F.heading, fontSize:18, marginBottom:12 }}>Hours by Station</div>
          <div style={{ height:220 }}>
            <ResponsiveContainer>
              <BarChart data={byStation}>
                <CartesianGrid stroke={C.line} strokeDasharray="3 3" vertical={false} />
                <XAxis dataKey="name" tick={{ fill:C.muted, fontSize:12 }} axisLine={{ stroke:C.line }} tickLine={false} />
                <YAxis tick={{ fill:C.muted, fontSize:12 }} axisLine={{ stroke:C.line }} tickLine={false} />
                <Tooltip cursor={{ fill:"#ffffff08" }} contentStyle={{ background:C.surface2, border:`1px solid ${C.line}`, borderRadius:8, color:C.text }} />
                <Bar dataKey="hours" radius={[4,4,0,0]}>
                  {byStation.map(d=><Cell key={d.name} fill={STATION_COLORS[d.name]||C.amber} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        {(!isWorker||visibility.worker.estVsActual)&&(
          <div style={s.card}>
            <div style={{ fontFamily:F.heading, fontSize:18, marginBottom:12 }}>Estimated vs Actual Hours</div>
            <div style={{ height:220 }}>
              <ResponsiveContainer>
                <BarChart data={estVsActual}>
                  <CartesianGrid stroke={C.line} strokeDasharray="3 3" vertical={false} />
                  <XAxis dataKey="name" tick={{ fill:C.muted, fontSize:10 }} axisLine={{ stroke:C.line }} tickLine={false} />
                  <YAxis tick={{ fill:C.muted, fontSize:12 }} axisLine={{ stroke:C.line }} tickLine={false} />
                  <Tooltip cursor={{ fill:"#ffffff08" }} contentStyle={{ background:C.surface2, border:`1px solid ${C.line}`, borderRadius:8, color:C.text }} />
                  <Bar dataKey="est"    fill={C.muted}  radius={[4,4,0,0]} name="Estimated" />
                  <Bar dataKey="actual" fill={C.amber} radius={[4,4,0,0]} name="Actual" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/* ============================================================ CONTROL PANEL */
function ControlPanel({ users, setUsers, settings, setSettings, visibility, setVisibility, timeEntries }) {
  const [tab,setTab]=useState("users"); const [nu,setNu]=useState({name:"",email:"",role:"worker"}); const [nd,setNd]=useState(""); const [showApi,setShowApi]=useState(false);

  const addUser=()=>{
    if(!nu.name||!nu.email.includes("@")) return;
    setUsers(u=>[...u,{id:"u"+Date.now(),provider:settings.authMode==="entra_only"?"entra":"standalone",active:true,...nu}]);
    setNu({name:"",email:"",role:"worker"});
  };

  const apiSample=JSON.stringify({ data:timeEntries.slice(0,3).map(e=>({id:e.id,subtask_id:e.subtaskId,user_id:e.userId,station:e.station,hours:e.hours})), meta:{page:1,per_page:100,total:timeEntries.length} },null,2);

  const tabs=[["users","Users & Roles"],["auth","Authentication"],["visibility","Field Visibility"],["api","REST API"]];

  return (
    <div>
      <SectionTitle kicker="System Configuration" title="Control Panel" />
      <TabBar tabs={tabs} active={tab} onChange={setTab} />

      {tab==="users"&&(
        <div style={{ display:"grid", gap:16 }}>
          <div style={s.cardNoPad}>
            <div style={{ display:"grid", gridTemplateColumns:"1.5fr 2fr 120px 80px 100px", gap:8, padding:"8px 16px", borderBottom:`1px solid ${C.line}`, fontSize:11, color:C.muted, fontWeight:700, textTransform:"uppercase", letterSpacing:"0.1em" }}>
              <span>Name</span><span>Email</span><span>Role</span><span>Auth</span><span></span>
            </div>
            {users.map(u=>(
              <div key={u.id} style={{ display:"grid", gridTemplateColumns:"1.5fr 2fr 120px 80px 100px", gap:8, padding:"10px 16px", borderBottom:`1px solid ${C.line}`, alignItems:"center", fontSize:13, opacity:u.active?1:0.45 }}>
                <span style={{ fontWeight:500 }}>{u.name}</span>
                <span style={{ color:C.muted, fontSize:12, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap" }}>{u.email}</span>
                <select value={u.role} onChange={e=>setUsers(us=>us.map(x=>x.id===u.id?{...x,role:e.target.value}:x))} style={{ ...s.input, width:"auto", padding:"4px 8px", fontSize:12 }}>
                  {["admin","supervisor","worker"].map(r=><option key={r} value={r}>{r}</option>)}
                </select>
                <span style={{ background:u.provider==="entra"?C.blueDim:C.surface2, color:u.provider==="entra"?C.blue:C.muted, padding:"2px 6px", borderRadius:4, fontSize:10, fontWeight:700, textTransform:"uppercase", textAlign:"center" }}>{u.provider}</span>
                <Btn small kind={u.active?"red":"green"} onClick={()=>setUsers(us=>us.map(x=>x.id===u.id?{...x,active:!x.active}:x))}>
                  {u.active?"Deactivate":"Restore"}
                </Btn>
              </div>
            ))}
          </div>
          <div style={s.card}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>Add User</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8 }}>
              <input value={nu.name} onChange={e=>setNu({...nu,name:e.target.value})} placeholder="Full name" style={{ ...s.input, flex:1, minWidth:140, width:"auto" }} />
              <input value={nu.email} onChange={e=>setNu({...nu,email:e.target.value})} placeholder="email@verabuilt.com" style={{ ...s.input, flex:1, minWidth:180, width:"auto" }} />
              <select value={nu.role} onChange={e=>setNu({...nu,role:e.target.value})} style={{ ...s.input, width:"auto", padding:"8px 12px" }}>
                {["admin","supervisor","worker"].map(r=><option key={r} value={r}>{r}</option>)}
              </select>
              <Btn kind="primary" onClick={addUser}>+ Add</Btn>
            </div>
            {nu.email&&!settings.domains.some(d=>nu.email.toLowerCase().endsWith("@"+d))&&(
              <div style={{ marginTop:8, color:C.amber, fontSize:12 }}>⚠ Email domain not in approved list — user will be blocked at login.</div>
            )}
          </div>
        </div>
      )}

      {tab==="auth"&&(
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill,minmax(300px,1fr))", gap:16 }}>
          <div style={s.card}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:12 }}>Authentication Mode</div>
            {[["both","Entra SSO + Standalone","Users may sign in either way."],["entra_only","Force Microsoft Entra","All users must use Entra SSO."],["standalone_only","Standalone Only","Email/password + TOTP only."]].map(([val,label,desc])=>(
              <button key={val} onClick={()=>setSettings(s=>({...s,authMode:val}))} style={{ width:"100%", textAlign:"left", padding:"12px", borderRadius:6, marginBottom:8, cursor:"pointer", background:settings.authMode===val?C.amberDim:C.surface2, border:`1px solid ${settings.authMode===val?C.amber:C.line}` }}>
                <div style={{ display:"flex", alignItems:"center", gap:8, fontWeight:600, fontSize:13, color:C.text, fontFamily:F.body }}>
                  <span style={{ width:10, height:10, borderRadius:"50%", background:settings.authMode===val?C.amber:C.line, display:"inline-block" }} />
                  {label}
                </div>
                <div style={{ fontSize:12, color:C.muted, marginTop:4, marginLeft:18 }}>{desc}</div>
              </button>
            ))}
          </div>
          <div style={s.card}>
            <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>Approved Sign-In Domains</div>
            <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>Only these email domains can authenticate.</div>
            <div style={{ display:"flex", flexWrap:"wrap", gap:8, marginBottom:12 }}>
              {settings.domains.map(d=>(
                <span key={d} style={{ display:"flex", alignItems:"center", gap:6, padding:"4px 10px", borderRadius:4, background:C.surface2, border:`1px solid ${C.line}`, fontSize:12, fontWeight:600 }}>
                  @{d}
                  <button onClick={()=>setSettings(s=>({...s,domains:s.domains.filter(x=>x!==d)}))} style={{ color:C.red, background:"none", border:"none", cursor:"pointer", fontSize:12 }}>✕</button>
                </span>
              ))}
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <input value={nd} onChange={e=>setNd(e.target.value)} placeholder="newdomain.com" style={{ ...s.input, flex:1, width:"auto" }} />
              <Btn kind="primary" onClick={()=>{if(nd.includes(".")){setSettings(s=>({...s,domains:[...s.domains,nd.toLowerCase().replace(/^@/,"")]}));setNd("");}}}>+ Approve</Btn>
            </div>
          </div>
        </div>
      )}

      {tab==="visibility"&&(
        <div style={s.card}>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>Field Visibility — Worker Role</div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:16 }}>Supervisors and admins always see everything.</div>
          {[["estVsActual","Estimated vs Actual chart","Show workers est/actual hour comparison."],["otherWorkers","Other workers' assignments","Show names on subtasks not assigned to viewer."],["laborCost","Labor cost fields","Hourly rate and cost roll-ups (hidden by default)."]].map(([key,label,desc])=>(
            <label key={key} style={{ display:"flex", alignItems:"flex-start", gap:12, padding:"12px", borderRadius:6, marginBottom:8, cursor:"pointer", background:C.surface2, border:`1px solid ${C.line}` }}>
              <input type="checkbox" checked={!!visibility.worker[key]} onChange={e=>setVisibility(v=>({...v,worker:{...v.worker,[key]:e.target.checked}}))} style={{ marginTop:2, accentColor:C.amber }} />
              <span>
                <span style={{ display:"block", fontSize:13, fontWeight:500, color:C.text }}>{label}</span>
                <span style={{ fontSize:12, color:C.muted }}>{desc}</span>
              </span>
            </label>
          ))}
        </div>
      )}

      {tab==="api"&&(
        <div style={s.card}>
          <div style={{ fontSize:14, fontWeight:600, marginBottom:4 }}>REST API — Power BI / External Integrations</div>
          <div style={{ fontSize:12, color:C.muted, marginBottom:16 }}>Versioned read endpoints secured by scoped API keys.</div>
          <div style={{ display:"grid", gap:8, marginBottom:16 }}>
            {[["GET","/api/v1/time-entries","All time entries · filter: ?station=&user_id=&from=&to="],["GET","/api/v1/sign-offs","Full two-step sign-off audit trail"],["GET","/api/v1/projects/:id/summary","Roll-up: hours, completion %, est-vs-actual by station"],["POST","/api/v1/keys","Issue scoped API key (admin only)"]].map(([m,path,desc])=>(
              <div key={path} style={{ display:"flex", flexWrap:"wrap", alignItems:"center", gap:10, padding:"8px 12px", borderRadius:6, background:C.surface2, border:`1px solid ${C.line}` }}>
                <span style={{ background:m==="GET"?C.greenDim:C.blueDim, color:m==="GET"?C.green:C.blue, padding:"2px 6px", borderRadius:4, fontSize:10, fontWeight:700, fontFamily:F.mono }}>{m}</span>
                <span style={{ fontFamily:F.mono, fontSize:12, color:C.text }}>{path}</span>
                <span style={{ fontSize:11, color:C.muted }}>{desc}</span>
              </div>
            ))}
          </div>
          <Btn small onClick={()=>setShowApi(!showApi)}>{showApi?"Hide":"Preview"} sample response</Btn>
          {showApi&&<pre style={{ marginTop:12, padding:12, borderRadius:6, fontSize:11, overflowX:"auto", background:"#080b10", border:`1px solid ${C.line}`, color:C.cyan, fontFamily:F.mono }}>{apiSample}</pre>}
        </div>
      )}
    </div>
  );
}

/* ============================================================ APP SHELL */
export default function App() {
  const [users,setUsers]=useState(SEED_USERS);
  const [session,setSession]=useState(null);
  const [tree,setTree]=useState(SEED_TREE);
  const [timeEntries,setTimeEntries]=useState(SEED_TIME);
  const [signOffs,setSignOffs]=useState(SEED_SIGNOFFS);
  const [timers,setTimers]=useState({});
  const [now,setNow]=useState(Date.now());
  const [nav,setNav]=useState("tracker");
  const [settings,setSettings]=useState({authMode:"both",domains:["verabuilt.com","fastbaysolutions.com"]});
  const [visibility,setVisibility]=useState({worker:{estVsActual:true,otherWorkers:true,laborCost:false}});

  useEffect(()=>{
    if(Object.keys(timers).length===0) return;
    const iv=setInterval(()=>setNow(Date.now()),1000);
    return ()=>clearInterval(iv);
  },[timers]);

  const addTime=(st,hours)=>{
    const f=findSubtask(tree,st.id);
    setTimeEntries(te=>[...te,{id:"te"+Date.now(),subtaskId:st.id,userId:session.id,station:f?.s.name||"—",hours:+hours.toFixed(2),day:"Today"}]);
  };
  const addSignOff=e=>setSignOffs(s=>[...s,{id:"so"+Date.now(),at:"Today "+new Date().toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}),...e}]);

  const GOOGLE_FONTS="https://fonts.googleapis.com/css2?family=Barlow+Condensed:wght@500;600;700&family=IBM+Plex+Sans:wght@400;500;600&family=JetBrains+Mono:wght@400;600;700&display=swap";

  if(!session) return (
    <>
      <link rel="stylesheet" href={GOOGLE_FONTS} />
      <div style={{ fontFamily:F.body, color:C.text, margin:0 }}>
        <LoginScreen users={users} settings={settings} onLogin={setSession} />
      </div>
    </>
  );

  const navItems=[
    ["tracker","Tracker",   ["admin","supervisor","worker"]],
    ["approvals","Sign-Offs",["admin","supervisor"]],
    ["dashboard","Dashboard",["admin","supervisor","worker"]],
    ["control","Control Panel",["admin"]],
  ].filter(([,,roles])=>roles.includes(session.role));

  const pendingCount=tree.flatMap(p=>p.units.flatMap(u=>u.stations.flatMap(s=>s.tasks.flatMap(t=>t.subtasks)))).filter(s=>s.status==="submitted").length;

  return (
    <>
      <link rel="stylesheet" href={GOOGLE_FONTS} />
      <div style={{ minHeight:"100vh", background:C.bg, color:C.text, fontFamily:F.body, margin:0 }}>
        <header style={{ position:"sticky", top:0, zIndex:20, padding:"10px 24px", display:"flex", alignItems:"center", gap:16, background:C.bg+"f2", backdropFilter:"blur(8px)", borderBottom:`1px solid ${C.line}` }}>
          <div style={{ fontFamily:F.heading, fontSize:22, lineHeight:1, letterSpacing:"0.01em" }}>
            VERA<span style={{ color:C.amber }}>TRACK</span>
          </div>
          <nav style={{ display:"flex", gap:4, flex:1, overflowX:"auto" }}>
            {navItems.map(([id,label])=>(
              <button key={id} onClick={()=>setNav(id)} style={{ padding:"6px 12px", borderRadius:6, fontSize:13, fontWeight:600, fontFamily:F.body, cursor:"pointer", whiteSpace:"nowrap", background:nav===id?C.amberDim:"transparent", color:nav===id?C.amber:C.muted, border:`1px solid ${nav===id?C.amber:"transparent"}`, position:"relative" }}>
                {label}
                {id==="approvals"&&pendingCount>0&&<span style={{ marginLeft:6, background:C.amber, color:"#171006", borderRadius:999, padding:"1px 6px", fontSize:10, fontWeight:700 }}>{pendingCount}</span>}
              </button>
            ))}
          </nav>
          <div style={{ textAlign:"right", display:"flex", flexDirection:"column", gap:2 }}>
            <span style={{ fontSize:12, fontWeight:600 }}>{session.name}</span>
            <span style={{ fontSize:10, color:C.amber, textTransform:"uppercase", letterSpacing:"0.12em" }}>{session.role}</span>
          </div>
          <Btn small onClick={()=>{setSession(null);setNav("tracker");}}>Sign Out</Btn>
        </header>

        <main style={{ padding:"24px", maxWidth:1100, margin:"0 auto" }}>
          {nav==="tracker"&&<Tracker user={session} tree={tree} setTree={setTree} timers={timers} setTimers={setTimers} now={now} addTime={addTime} addSignOff={addSignOff} signOffs={signOffs} />}
          {nav==="approvals"&&<Approvals user={session} tree={tree} setTree={setTree} signOffs={signOffs} addSignOff={addSignOff} users={users} />}
          {nav==="dashboard"&&<Dashboard user={session} tree={tree} timeEntries={timeEntries} users={users} visibility={visibility} />}
          {nav==="control"&&session.role==="admin"&&<ControlPanel users={users} setUsers={setUsers} settings={settings} setSettings={setSettings} visibility={visibility} setVisibility={setVisibility} timeEntries={timeEntries} />}
        </main>

        <footer style={{ padding:"16px 24px", textAlign:"center", fontSize:11, color:C.muted, borderTop:`1px solid ${C.line}` }}>
          VeraTrack Demo · Fast Bay Solutions LLC · In-memory demo — no backend connected
        </footer>
      </div>
    </>
  );
}
